/**
 * ============================================================================
 * useBatchSynthesis - AI processing submission after document upload
 * ============================================================================
 * 
 * This hook exposes two paths:
 * - finalizeAfterUpload: default path after document storage succeeds. The
 *   backend counts source documents, then either submits AI jobs or records a
 *   durable dossier-level hold.
 * - submitBatches: legacy/retry path. It still goes through the same backend
 *   gate and must preserve a held response as a business state, not an error.
 * 
 * ARCHITECTURE:
 * Frontend (this hook) → Backend (async processing) → Database (updates)
 *                                                    ↑
 * Frontend (useSynthesisStatus) ← Database (polls) ←┘
 * 
 * USAGE:
 * ```tsx
 * const { submitBatches, isSubmitting } = useBatchSynthesis(caseId);
 * 
 * // After uploading files:
 * await finalizeAfterUpload(uploadedDocs, metadata, { generateTitle: true });
 * ```
 * 
 * TITLE GENERATION:
 * - generateTitle: true  → NoTitreModifications: false → Titles generated
 * - generateTitle: false → NoTitreModifications: true  → Titles preserved
 * 
 * ============================================================================
 */

import { useState, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { SynthesisService } from '@/services/synthesisService';
import { ErrorService } from '@/lib/errors/ErrorService';
import { useSynthesisStore } from '@/stores/synthesisStore';
import { saveBatchJobMappings } from '@/utils/batchJobMapping';
import type { CaseMetadata } from '@/services/folderHierarchyService';
import type { AiProcessingGateResponse } from '@hector/shared-types';
import { useCurrentCabinet } from '@/hooks/cabinet/useCabinetCore';

const AI_PROCESSING_BATCH_SIZE = 5;

/**
 * The gate currently returns one job id per five-document backend batch, not a
 * document-level mapping. Keep the assumption isolated until the API exposes
 * the explicit relation, and fail closed if the response is incomplete.
 */
export function mapSubmittedDocumentsToJobs(
  submittedDocumentIds: string[],
  jobIds: string[],
): Array<{ documentId: string; jobId: string }> {
  return submittedDocumentIds.map((documentId, index) => {
    const batchIndex = Math.floor(index / AI_PROCESSING_BATCH_SIZE);
    const jobId = jobIds[batchIndex];

    if (!jobId) {
      throw new Error(
        `AI processing response is missing a job ID for submitted batch ${batchIndex + 1}`,
      );
    }

    return { documentId, jobId };
  });
}

interface BatchSubmissionOptions {
  category: string;
  subcategory?: string;
  chunkSize?: number;
  generateTitle?: boolean; // If false, sets NoTitreModifications=true for entire batch
  onSubmissionComplete?: (successCount: number, failedCount: number) => void;
}

interface FinalizeAfterUploadOptions {
  generateTitle?: boolean;
  onSubmissionComplete?: (successCount: number, failedCount: number) => void;
}

interface BatchSubmissionOutcome {
  submittedCount: number;
  heldCount: number;
  failedCount: number;
}

interface UseBatchSynthesisReturn {
  /**
   * Submit documents for batch synthesis
   * Backend processes asynchronously, existing badges poll database for completion
   */
  submitBatches: (
    uploadedDocs: Array<{ documentId: string; fileName: string }>,
    metadata: CaseMetadata,
    options: BatchSubmissionOptions
  ) => Promise<BatchSubmissionOutcome>;

  /** Finalizes AI processing without re-uploading documents or bypassing the gate. */
  finalizeAfterUpload: (
    uploadedDocs: Array<{ documentId: string; fileName: string }>,
    metadata: CaseMetadata,
    options: FinalizeAfterUploadOptions,
  ) => Promise<AiProcessingGateResponse | null>;
  
  /**
   * Whether batch submission is in progress
   */
  isSubmitting: boolean;
}

export function useBatchSynthesis(caseId: string): UseBatchSynthesisReturn {
  const synthesisServiceRef = useRef(new SynthesisService());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const { data: currentCabinet } = useCurrentCabinet();
  const cabinetId = currentCabinet?.id;
  
  /**
   * SIMPLIFIED: Just submit batches and mark as processing
   * Database polling (useSynthesisStatus) handles completion detection
   */
  const submitBatches = useCallback(async (
    uploadedDocs: Array<{ documentId: string; fileName: string }>,
    metadata: CaseMetadata,
    options: BatchSubmissionOptions
  ) => {
    console.log(`[Batch] Submitting ${uploadedDocs.length} documents for synthesis`);
    
    // Validation
    if (!metadata.dossierName || metadata.dossierName.trim() === '') {
      ErrorService.handle(new Error('Missing folder name for synthesis'), { context: { action: 'batch_synthesis_validation' } });
      return { submittedCount: 0, heldCount: 0, failedCount: uploadedDocs.length };
    }
    
    try {
      setIsSubmitting(true);

      // Group into batches of 5
      // Why: smaller batches reduce blast radius if a worker crashes mid-batch
      // (a single stalled job orphans 5 pieces instead of 10), and improve
      // fairness across cabinets in the shared processing queue.
      const BATCH_SIZE = 5;
      const batches: Array<Array<{ documentId: string; fileName: string }>> = [];
      
      for (let i = 0; i < uploadedDocs.length; i += BATCH_SIZE) {
        batches.push(uploadedDocs.slice(i, i + BATCH_SIZE));
      }
      
      console.log(`[Batch] Grouped into ${batches.length} batch(es) of max ${BATCH_SIZE}`);
      
      // Show toast for multiple batches
      if (batches.length > 1) {
        toast.info(`Soumission de ${uploadedDocs.length} document(s) en ${batches.length} lot(s)...`);
      }

      // Get store actions for UI feedback
      const { setProcessing, setFailed, clearDocumentState } = useSynthesisStore.getState();

      // Submit all batches in parallel
      const submissionPromises = batches.map(async (batch, batchIndex) => {
        try {
          // Build complete file payload with all required metadata per-file
          const batchFiles = batch.map(({ documentId }) => ({
            documentID: documentId,
            dossierID: caseId,
            dossierName: metadata.dossierName,
            clientName: metadata.clientName || 'Client non spécifié',
            lawyerName: metadata.lawyerName || 'Non assigné',
            typeDeProcedure: metadata.typeDeProcedure || 'Type non spécifié',
            documentCategory: options.category,
            documentSubcategory: options.subcategory || undefined,
            enableVectorization: true,
            vectorizeOriginal: true,
            vectorizeSynthesis: true,
            chunkSize: options.chunkSize || 8000,
            NoTitreModifications: options.generateTitle === false
          }));
          
          const result = await synthesisServiceRef.current.submitSynthesisBatch(
            batchFiles,
            caseId,
            {
              documentCategory: options.category,
              documentSubcategory: options.subcategory,
              chunkSize: options.chunkSize || 8000
            }
          );
          
          if (!result.success) {
            console.error(`[Batch] Batch ${batchIndex + 1} submission failed:`, result.error);
            batch.forEach(({ documentId }) => setFailed(documentId, result.error));
            return { success: false, batchIndex, error: result.error };
          }
          
          if (result.status === 'held') {
            console.log(`[Batch] Batch ${batchIndex + 1} held by dossier AI processing limit`);
            batch.forEach(({ documentId }) => clearDocumentState(documentId));
            return { success: true, held: true, batchIndex };
          }

          if (!result.jobId) {
            console.error(`[Batch] No job ID returned for batch ${batchIndex + 1}`);
            batch.forEach(({ documentId }) => setFailed(documentId, 'No job ID'));
            return { success: false, batchIndex, error: 'No job ID' };
          }
          
          console.log(`[Batch] Batch ${batchIndex + 1} submitted successfully: ${result.jobId}`);
          
          batch.forEach(({ documentId }) => {
            setProcessing(documentId, result.jobId);
          });

          saveBatchJobMappings(
            batch.map(({ documentId }) => ({ documentId, jobId: result.jobId! }))
          );
          
          return { success: true, batchIndex, jobId: result.jobId };
          
        } catch (error) {
          ErrorService.handle(error, { context: { action: 'batch_synthesis_submit' }, silent: true });
          batch.forEach(({ documentId }) => {
            setFailed(documentId, error instanceof Error ? error.message : 'Unknown error');
          });
          return { success: false, batchIndex, error: error instanceof Error ? error.message : 'Unknown error' };
        }
      });
      
      // Wait for all submissions
      const results = await Promise.all(submissionPromises);
      
      // Collect results
      const heldCount = results.filter(r => r.success && 'held' in r && r.held === true).length;
      const successCount = results.filter(r => r.success && !('held' in r && r.held === true)).length;
      const failedCount = results.filter(r => !r.success).length;
      
      console.log(`[Batch] Submission complete: ${successCount} submitted, ${heldCount} held, ${failedCount} failed`);
      
      // Show appropriate toast
      if (successCount > 0) {
        if (failedCount === 0) {
          toast.success(`${uploadedDocs.length} document(s) soumis - traitement en cours`);
        } else {
          toast.warning(`${successCount} lot(s) soumis, ${failedCount} échoué(s)`);
        }
      }

      if (heldCount > 0) {
        toast.info('Le traitement IA ne peut pas être lancé car vous avez plus de 2 000 pièces dans ce dossier.');
      }
      
      if (failedCount > 0 && successCount === 0) {
        ErrorService.handle(new Error('All synthesis batches failed'), { context: { action: 'batch_synthesis_all_failed' } });
      }

      if (heldCount > 0) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['case-documents', caseId] }),
          queryClient.invalidateQueries({ queryKey: ['case-documents'] }),
          queryClient.invalidateQueries({ queryKey: ['folder-contents', caseId] }),
          queryClient.invalidateQueries({ queryKey: ['folder-contents'] }),
          ...(cabinetId
            ? [queryClient.invalidateQueries({ queryKey: ['document-ai-processing-summary', cabinetId, caseId] })]
            : []),
          queryClient.invalidateQueries({ queryKey: ['document-ai-processing-summary', caseId] }),
          queryClient.invalidateQueries({ queryKey: ['document-ai-processing-summary'] }),
        ]);
      }
      
      // Call completion callback
      if (options.onSubmissionComplete) {
        options.onSubmissionComplete(successCount, failedCount);
      }

      return {
        submittedCount: successCount,
        heldCount,
        failedCount,
      };
      
    } catch (error) {
      ErrorService.handle(error, { context: { action: 'batch_synthesis_critical' } });
      return { submittedCount: 0, heldCount: 0, failedCount: uploadedDocs.length };
    } finally {
      setIsSubmitting(false);
    }
  }, [cabinetId, caseId, queryClient]);

  const finalizeAfterUpload = useCallback(async (
    uploadedDocs: Array<{ documentId: string; fileName: string }>,
    metadata: CaseMetadata,
    options: FinalizeAfterUploadOptions,
  ): Promise<AiProcessingGateResponse | null> => {
    if (uploadedDocs.length === 0) return null;

    if (!metadata.dossierName || metadata.dossierName.trim() === '') {
      ErrorService.handle(new Error('Cannot finalize AI processing: the dossier name is missing'), {
        context: { action: 'ai_processing_finalize_validation', case_id: caseId },
      });
      return null;
    }

    try {
      setIsSubmitting(true);
      const response = await synthesisServiceRef.current.finalizeAiProcessingAfterUpload(caseId, {
        documentIds: uploadedDocs.map(({ documentId }) => documentId),
        caseMetadata: metadata,
        generateTitle: options.generateTitle ?? true,
      });

      if (response.status === 'submitted') {
        const mappings = mapSubmittedDocumentsToJobs(
          response.submittedDocumentIds,
          response.jobIds,
        );
        const { setProcessing } = useSynthesisStore.getState();

        mappings.forEach(({ documentId, jobId }) => setProcessing(documentId, jobId));
        saveBatchJobMappings(mappings);
        toast.success(`${response.submittedDocumentIds.length} document(s) soumis - traitement en cours`);
        options.onSubmissionComplete?.(response.submittedDocumentIds.length, 0);
      } else {
        toast.info('Le traitement IA ne peut pas être lancé car vous avez plus de 2 000 pièces dans ce dossier.');
        options.onSubmissionComplete?.(0, 0);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['case-documents', caseId] }),
        queryClient.invalidateQueries({ queryKey: ['case-documents'] }),
        queryClient.invalidateQueries({ queryKey: ['folder-contents', caseId] }),
        queryClient.invalidateQueries({ queryKey: ['folder-contents'] }),
        ...(cabinetId
          ? [queryClient.invalidateQueries({ queryKey: ['document-ai-processing-summary', cabinetId, caseId] })]
          : []),
        queryClient.invalidateQueries({ queryKey: ['document-ai-processing-summary', caseId] }),
        queryClient.invalidateQueries({ queryKey: ['document-ai-processing-summary'] }),
      ]);

      return response;
    } catch (error) {
      ErrorService.handle(error, {
        context: { action: 'ai_processing_finalize_after_upload', case_id: caseId },
      });
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, [cabinetId, caseId, queryClient]);
  
  return {
    submitBatches,
    finalizeAfterUpload,
    isSubmitting
  };
}
